/**
 * @module constants
 * @package @geenius/adapters/shared
 * @description Defines the shared adapter domains, provider registries, status
 * labels, and UI metadata used across every runtime variant. Explicit unions
 * preserve compile-time parity for providers, domains, and statuses.
 */

import type { AdapterDomain } from "./types";

function freezeRegistryItems<T extends object>(
  items: readonly T[],
): readonly Readonly<T>[] {
  const frozenItems = items.map(
    (item) => Object.freeze({ ...item }) as Readonly<T>,
  );
  return Object.freeze(frozenItems);
}

function cloneRegistryItem<T extends object>(item: Readonly<T>): T {
  return { ...item } as T;
}

// ─── Adapter Domains ─────────────────────────────────────────────────────────

/**
 * Ordered list of all supported adapter domains.
 * These represent the major functional areas that can have pluggable implementations.
 */
export const ADAPTER_DOMAINS: readonly AdapterDomain[] = [
  "db",
  "auth",
  "payments",
  "ai",
  "storage",
  "admin",
  "logger",
];

// ─── Provider Names per Domain ───────────────────────────────────────────────

/**
 * Type representing valid database provider names for adapter configuration
 * and resolver registration.
 *
 * The v1 public provider subpaths are limited to `convex`, `neon`,
 * `cloudflareKV`, and `memory`; future and BYOD ids remain metadata/resolver
 * keys only.
 */
export type DbProviderName =
  | "localStorage"
  | "convex"
  | "supabase"
  | "drizzle"
  | "neon"
  | "prisma"
  | "kysely"
  | "mongodb"
  | "cloudflareKV"
  | "memory";
/** Type representing valid authentication provider names */
export type AuthProviderName =
  | "localStorage"
  | "better-auth"
  | "convex-auth"
  | "clerk"
  | "supabase-auth";
/** Type representing valid AI provider names */
export type AiProviderName =
  | "localStorage"
  | "openai"
  | "anthropic"
  | "gemini"
  | "ollama"
  | "cloudflare-ai-gateway"
  | "vercel-ai-sdk";
/** Type representing valid file storage provider names */
export type StorageProviderName =
  | "localStorage"
  | "r2"
  | "s3"
  | "uploadthing"
  | "supabase-storage"
  | "convex-storage"
  | "minio";
/** Type representing valid payment provider names */
export type PaymentProviderName =
  | "localStorage"
  | "stripe"
  | "lemonsqueezy"
  | "polar"
  | "noop";
/** Type representing valid admin provider names */
export type AdminProviderName = "localStorage";
/** Type representing valid logger provider names */
export type LoggerProviderName = "console" | "memory";

/**
 * Available database providers (ordered from lightweight to production-ready).
 * Supports local mocks and the four launch provider contracts without importing
 * downstream SDKs into this package.
 */
export const DB_PROVIDERS: readonly DbProviderName[] = [
  "localStorage",
  "convex",
  "supabase",
  "drizzle",
  "neon",
  "prisma",
  "kysely",
  "mongodb",
  "cloudflareKV",
  "memory",
];

/** Canonical database backends that define the expanded boilerplate universe. */
export type EcosystemDbProviderName =
  | "convex"
  | "neon"
  | "cloudflareKV"
  | "memory"
  | "supabase"
  | "mongodb"
  | "drizzle"
  | "prisma"
  | "kysely";

/** Four database backend categories used by scaffold tooling and setup UIs. */
export type EcosystemDbProviderCategory =
  | "platform-native"
  | "edge-kv"
  | "in-process"
  | "byod-orm";

/** Launch readiness for a database backend in the expanded ecosystem. */
export type EcosystemDbProviderStage = "launch" | "future" | "byod";

/** Metadata for production database backends in the expanded ecosystem. */
export interface EcosystemDbProviderMeta {
  /** Canonical provider id used in adapter config. */
  id: EcosystemDbProviderName;
  /** Provider family: vendor SDK/native platform versus bring-your-own ORM. */
  category: EcosystemDbProviderCategory;
  /** Whether this backend is part of the current four-provider launch matrix. */
  stage: EcosystemDbProviderStage;
  /** Runtime SDK or ORM package owned by the downstream provider implementation. */
  sdk: string;
  /** Optional default database driver used by the downstream provider implementation. */
  defaultDriver?: string;
  /** Expected auth integration for boilerplate scaffolds using this backend. */
  authIntegration: string;
  /** Schema or data-model artifact expected in generated boilerplates. */
  schema: string;
  /** Factory exported by @geenius/db for this provider family. */
  factory: string;
}

/**
 * Platform-native database backends tracked for ecosystem metadata.
 *
 * Only entries whose metadata stage is `launch` are v1 provider subpaths.
 */
export const PLATFORM_NATIVE_DB_PROVIDERS: readonly EcosystemDbProviderName[] =
  ["convex", "neon", "supabase", "mongodb"];

/** BYOD ORM resolver metadata ids tracked for downstream DB adapter factories. */
export const BYOD_ORM_DB_PROVIDERS: readonly EcosystemDbProviderName[] = [
  "drizzle",
  "prisma",
  "kysely",
];

/** Database backends included in the current launch matrix. */
export const LAUNCH_DB_PROVIDERS: readonly EcosystemDbProviderName[] = [
  "convex",
  "neon",
  "cloudflareKV",
  "memory",
];

/** Future DB providers are metadata-only until a coordinated axis expansion. */
export const FUTURE_DB_PROVIDERS: readonly EcosystemDbProviderName[] = [
  "supabase",
  "mongodb",
];

/** Full production database backend universe tracked for launch and later expansion. */
export const ECOSYSTEM_DB_PROVIDERS: readonly EcosystemDbProviderMeta[] =
  freezeRegistryItems<EcosystemDbProviderMeta>([
    {
      id: "convex",
      category: "platform-native",
      stage: "launch",
      sdk: "convex",
      authIntegration: "Convex Auth",
      schema: "ConvexDB schema",
      factory: "createConvexDbAdapter",
    },
    {
      id: "neon",
      category: "platform-native",
      stage: "launch",
      sdk: "@neondatabase/serverless",
      authIntegration: "Better Auth",
      schema: "SQL migrations",
      factory: "createNeonNativeDbAdapter",
    },
    {
      id: "cloudflareKV",
      category: "edge-kv",
      stage: "launch",
      sdk: "Cloudflare Workers KV",
      authIntegration: "Better Auth / session cache",
      schema: "KV namespace keys with TTL metadata",
      factory: "createCloudflareKvDbAdapter",
    },
    {
      id: "memory",
      category: "in-process",
      stage: "launch",
      sdk: "Map",
      authIntegration: "Test/local-only",
      schema: "In-process collections",
      factory: "createMemoryDbAdapter",
    },
    {
      id: "supabase",
      category: "platform-native",
      stage: "future",
      sdk: "@supabase/supabase-js",
      defaultDriver: "postgres",
      authIntegration: "Supabase Auth",
      schema: "SQL migrations plus storage/auth metadata",
      factory: "createSupabaseDbAdapter",
    },
    {
      id: "mongodb",
      category: "platform-native",
      stage: "future",
      sdk: "mongodb",
      authIntegration: "Better Auth",
      schema: "Document collections and indexes",
      factory: "createMongoDbAdapter",
    },
    {
      id: "drizzle",
      category: "byod-orm",
      stage: "byod",
      sdk: "drizzle-orm",
      defaultDriver: "postgres",
      authIntegration: "App-owned",
      schema: "App-owned Drizzle schema",
      factory: "createDrizzleDbAdapter",
    },
    {
      id: "prisma",
      category: "byod-orm",
      stage: "byod",
      sdk: "prisma",
      authIntegration: "App-owned",
      schema: "App-owned Prisma schema",
      factory: "createPrismaDbAdapter",
    },
    {
      id: "kysely",
      category: "byod-orm",
      stage: "byod",
      sdk: "kysely",
      authIntegration: "App-owned",
      schema: "App-owned Kysely table definitions",
      factory: "createKyselyDbAdapter",
    },
  ]);

/**
 * Available authentication providers.
 * Ranges from mock implementations (localStorage) to full-featured solutions (Clerk, Supabase).
 */
export const AUTH_PROVIDERS: readonly AuthProviderName[] = [
  "localStorage",
  "better-auth",
  "convex-auth",
  "clerk",
  "supabase-auth",
];

/**
 * Available AI/LLM providers.
 * Includes major API providers (OpenAI, Anthropic) and local options (Ollama).
 */
export const AI_PROVIDERS: readonly AiProviderName[] = [
  "localStorage",
  "openai",
  "anthropic",
  "gemini",
  "ollama",
  "cloudflare-ai-gateway",
  "vercel-ai-sdk",
];

/**
 * Available file storage providers.
 * Supports in-browser storage, cloud providers, and self-hosted options.
 */
export const STORAGE_PROVIDERS: readonly StorageProviderName[] = [
  "localStorage",
  "r2",
  "s3",
  "uploadthing",
  "supabase-storage",
  "convex-storage",
  "minio",
];

/**
 * Available payment processing providers.
 * Includes full-featured (Stripe) and mock implementations for testing.
 */
export const PAYMENT_PROVIDERS: readonly PaymentProviderName[] = [
  "localStorage",
  "stripe",
  "lemonsqueezy",
  "polar",
  "noop",
];

/**
 * Available admin panel providers.
 * Currently limited to localStorage for development/prototyping.
 */
export const ADMIN_PROVIDERS: readonly AdminProviderName[] = ["localStorage"];
/**
 * Available logger providers.
 * Starts with the local-first console logger while preserving room for
 * dedicated downstream logging packages to register production sinks later.
 */
export const LOGGER_PROVIDERS: readonly LoggerProviderName[] = [
  "console",
  "memory",
];

// ─── Status Types ────────────────────────────────────────────────────────────

/**
 * Valid adapter connection states.
 * Used to represent the health and availability of configured adapters.
 */
/** Type representing valid adapter status values */
export type AdapterStatus =
  | "connected"
  | "disconnected"
  | "error"
  | "initializing";
export const ADAPTER_STATUSES: readonly AdapterStatus[] = [
  "connected",
  "disconnected",
  "error",
  "initializing",
];

// ─── Domain Labels ───────────────────────────────────────────────────────────

/**
 * Human-readable labels for each adapter domain.
 * Used in UI components like dropdowns, headings, and breadcrumbs.
 */
export const DOMAIN_LABELS: Record<AdapterDomain, string> = {
  db: "Database",
  auth: "Authentication",
  payments: "Payments",
  ai: "AI & LLM",
  storage: "File Storage",
  admin: "Admin Panel",
  logger: "Logger",
};

/**
 * Semantic icon identifiers for each adapter domain.
 * UI variants map these descriptors to native icon components or tokenized
 * icon slots instead of rendering emoji glyphs from shared metadata.
 */
export const DOMAIN_ICONS: Record<AdapterDomain, string> = {
  db: "database",
  auth: "shield-check",
  payments: "credit-card",
  ai: "sparkles",
  storage: "folder",
  admin: "settings",
  logger: "file-text",
};

/**
 * Detailed descriptions of each adapter domain.
 * Displayed in help text, tooltips, and onboarding flows.
 */
export const DOMAIN_DESCRIPTIONS: Record<AdapterDomain, string> = {
  db: "Connect to your preferred database backend for data persistence.",
  auth: "User authentication, sessions, and identity management.",
  payments: "Subscriptions, checkout, and billing management.",
  ai: "AI completions, chat, and embeddings via the selected LLM provider.",
  storage:
    "Upload, download, and manage files with the selected storage provider.",
  admin: "Admin dashboard metrics and user management.",
  logger:
    "Structured logs, child loggers, and downstream observability routing.",
};

// ─── Provider Metadata ───────────────────────────────────────────────────────

/**
 * Metadata about a specific provider implementation.
 * Contains information needed for provider selection UI, documentation links, and tier gating.
 */
export interface ProviderMeta {
  /** Unique identifier for the provider (matches provider name in config) */
  id: string;
  /** Human-readable provider name for display in UI */
  name: string;
  /** The domain this provider belongs to */
  domain: AdapterDomain;
  /** Brief description of what this provider offers */
  description: string;
  /** Minimum feature tier required to use this provider (tier gating) */
  tier: "pronto" | "lancio" | "studio";
  /** Whether the provider typically needs an API key or token during setup */
  requiresApiKey?: boolean;
  /** Whether the provider typically needs a configurable base URL during setup */
  requiresBaseUrl?: boolean;
  /** Optional URL to provider documentation or setup guide */
  docUrl?: string;
}

/**
 * Comprehensive registry of all available providers across all domains.
 * Used for provider selection UI, documentation, and availability checking.
 * Organized by domain with localStorage typically first for dev/mock workflows.
 */
export const PROVIDER_REGISTRY: readonly Readonly<ProviderMeta>[] =
  freezeRegistryItems<ProviderMeta>([
    // DB
    {
      id: "localStorage",
      name: "localStorage",
      domain: "db",
      description: "In-browser mock storage for prototyping",
      tier: "pronto",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "convex",
      name: "Convex",
      domain: "db",
      description: "Real-time cloud database with sub-50ms reactivity",
      tier: "lancio",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "supabase",
      name: "Supabase",
      domain: "db",
      description: "PostgreSQL + Auth + Storage",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: true,
    },
    {
      id: "drizzle",
      name: "Drizzle",
      domain: "db",
      description: "Drizzle ORM adapter via the downstream DB provider package",
      tier: "lancio",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "neon",
      name: "Neon",
      domain: "db",
      description:
        "Native Neon serverless PostgreSQL via the downstream DB provider package",
      tier: "lancio",
      requiresApiKey: false,
      requiresBaseUrl: true,
    },
    {
      id: "prisma",
      name: "Prisma",
      domain: "db",
      description:
        "Prisma Client adapter via the downstream DB provider package",
      tier: "lancio",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "kysely",
      name: "Kysely",
      domain: "db",
      description:
        "Kysely query builder adapter via the downstream DB provider package",
      tier: "lancio",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "mongodb",
      name: "MongoDB",
      domain: "db",
      description:
        "MongoDB document database via the downstream DB provider package",
      tier: "lancio",
      requiresApiKey: false,
      requiresBaseUrl: true,
    },
    {
      id: "cloudflareKV",
      name: "Cloudflare KV",
      domain: "db",
      description:
        "Edge key-value storage via the downstream DB provider package",
      tier: "studio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "memory",
      name: "Memory",
      domain: "db",
      description:
        "Ephemeral in-memory adapter for tests and local development",
      tier: "studio",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    // Auth
    {
      id: "localStorage",
      name: "localStorage",
      domain: "auth",
      description: "Mock auth for prototyping",
      tier: "pronto",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "better-auth",
      name: "Better Auth",
      domain: "auth",
      description: "Self-hosted auth with OAuth + MFA",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "convex-auth",
      name: "Convex Auth",
      domain: "auth",
      description: "Built-in Convex sessions",
      tier: "lancio",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "clerk",
      name: "Clerk",
      domain: "auth",
      description: "SaaS auth with pre-built UI",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "supabase-auth",
      name: "Supabase Auth",
      domain: "auth",
      description: "Built-in Supabase auth",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: true,
    },
    // AI
    {
      id: "localStorage",
      name: "localStorage",
      domain: "ai",
      description: "Mock AI for prototyping",
      tier: "pronto",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "openai",
      name: "OpenAI",
      domain: "ai",
      description: "GPT models with streaming + embeddings",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "anthropic",
      name: "Anthropic",
      domain: "ai",
      description: "Claude models with streaming",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "gemini",
      name: "Google Gemini",
      domain: "ai",
      description: "Gemini models, zero npm deps",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "ollama",
      name: "Ollama",
      domain: "ai",
      description: "Local LLMs, zero npm deps",
      tier: "lancio",
      requiresApiKey: false,
      requiresBaseUrl: true,
    },
    {
      id: "cloudflare-ai-gateway",
      name: "CF AI Gateway",
      domain: "ai",
      description: "Caching/rate-limiting proxy",
      tier: "studio",
      requiresApiKey: true,
      requiresBaseUrl: true,
    },
    {
      id: "vercel-ai-sdk",
      name: "Vercel AI SDK",
      domain: "ai",
      description: "generateText/embed wrappers",
      tier: "studio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    // Storage
    {
      id: "localStorage",
      name: "localStorage",
      domain: "storage",
      description: "Base64 in-browser storage",
      tier: "pronto",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "r2",
      name: "Cloudflare R2",
      domain: "storage",
      description: "S3-compatible, zero egress",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "s3",
      name: "AWS S3",
      domain: "storage",
      description: "IAM roles + CloudFront CDN",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "uploadthing",
      name: "Uploadthing",
      domain: "storage",
      description: "REST API file upload SaaS",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "supabase-storage",
      name: "Supabase Storage",
      domain: "storage",
      description: "Native Supabase storage",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: true,
    },
    {
      id: "convex-storage",
      name: "Convex Storage",
      domain: "storage",
      description: "Convex built-in file storage",
      tier: "lancio",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "minio",
      name: "Minio",
      domain: "storage",
      description: "Self-hosted S3-compatible",
      tier: "studio",
      requiresApiKey: true,
      requiresBaseUrl: true,
    },
    // Payments
    {
      id: "localStorage",
      name: "localStorage",
      domain: "payments",
      description: "Mock billing for prototyping",
      tier: "pronto",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "stripe",
      name: "Stripe",
      domain: "payments",
      description: "Full billing, subscriptions, checkout",
      tier: "lancio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "lemonsqueezy",
      name: "LemonSqueezy",
      domain: "payments",
      description:
        "Merchant-of-record subscriptions with SaaS-focused billing flows",
      tier: "studio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "polar",
      name: "Polar",
      domain: "payments",
      description:
        "Developer-first billing and subscriptions with product-led pricing flows",
      tier: "studio",
      requiresApiKey: true,
      requiresBaseUrl: false,
    },
    {
      id: "noop",
      name: "Noop",
      domain: "payments",
      description: "No-op adapter — always returns success",
      tier: "pronto",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    // Admin
    {
      id: "localStorage",
      name: "localStorage",
      domain: "admin",
      description: "Mock admin for prototyping",
      tier: "pronto",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    // Logger
    {
      id: "console",
      name: "Console",
      domain: "logger",
      description:
        "Console-backed structured logger for prototyping and local development",
      tier: "pronto",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
    {
      id: "memory",
      name: "Memory",
      domain: "logger",
      description:
        "In-memory structured logger for tests, Storybook, and offline prototypes",
      tier: "pronto",
      requiresApiKey: false,
      requiresBaseUrl: false,
    },
  ]);

/**
 * Get all available providers for a specific adapter domain.
 *
 * @param domain - The adapter domain to get providers for
 * @returns Array of provider metadata for the given domain, ordered by tier
 *
 * @example
 * const dbProviders = getProvidersForDomain('db');
 * // Returns metadata for localStorage, Convex, Neon, Cloudflare KV, and Memory.
 */
export function getProvidersForDomain(domain: AdapterDomain): ProviderMeta[] {
  return PROVIDER_REGISTRY.filter((p) => p.domain === domain).map(
    cloneRegistryItem,
  );
}

/**
 * Get expanded ecosystem database providers by backend category.
 *
 * @param category - Backend category to filter by
 * @returns Production database provider metadata for the selected category
 */
export function getEcosystemDbProvidersByCategory(
  category: EcosystemDbProviderCategory,
): EcosystemDbProviderMeta[] {
  return ECOSYSTEM_DB_PROVIDERS.filter(
    (provider) => provider.category === category,
  ).map(cloneRegistryItem);
}

/**
 * Get expanded ecosystem database providers by launch readiness.
 *
 * @param stage - Launch stage to filter by
 * @returns Production database provider metadata for the selected stage
 */
export function getEcosystemDbProvidersByStage(
  stage: EcosystemDbProviderStage,
): EcosystemDbProviderMeta[] {
  return ECOSYSTEM_DB_PROVIDERS.filter(
    (provider) => provider.stage === stage,
  ).map(cloneRegistryItem);
}

/**
 * Get expanded ecosystem metadata for a database provider.
 *
 * @param providerId - Canonical database provider id
 * @returns Expanded ecosystem metadata if the provider is part of the nine-backend matrix
 */
export function getEcosystemDbProviderMeta(
  providerId: string,
): EcosystemDbProviderMeta | undefined {
  const provider = ECOSYSTEM_DB_PROVIDERS.find(
    (provider) => provider.id === providerId,
  );
  return provider ? cloneRegistryItem(provider) : undefined;
}

/**
 * Get metadata for a specific provider implementation.
 *
 * @param domain - The adapter domain the provider belongs to
 * @param providerId - The unique identifier of the provider
 * @returns Provider metadata if found, undefined otherwise
 *
 * @example
 * const stripeMeta = getProviderMeta('payments', 'stripe');
 * if (stripeMeta?.tier === 'lancio') {
 *   // Stripe is available on lancio tier
 * }
 */
export function getProviderMeta(
  domain: AdapterDomain,
  providerId: string,
): ProviderMeta | undefined {
  const provider = PROVIDER_REGISTRY.find(
    (p) => p.domain === domain && p.id === providerId,
  );
  return provider ? cloneRegistryItem(provider) : undefined;
}
